import {
  Flex,
  FlexColumn,
  ListContainer,
  CloseInfo,
  useModelFormUpdate,
  errorsToPropertyErrors,
  InputGroup,
} from '@equifinance/shared';
import { v4 as uuid } from 'uuid';
import styled from 'styled-components';
import { useState } from 'react';
import { Stages } from '../../../../types/Stages';
import { stageOnOrAfter } from '../../../../contexts/helpers';
import { AccountTitle } from '../../components/AccountTitle';
import { StaticDataItem } from '../../../../api/useAccountApi';
import { PlusIcon } from '../../components/Icons';
import { DatedAddress, Applicant } from '../../../../contexts/account/Account';
import { useAccountContext } from '../../../../contexts';
import AddressHistoryItem from './AddressHistoryItem';
import { PaddedSection } from '../../components/Section';
import { useToast } from '../../../../hooks/useToast';

const AddAddressContainer = styled(Flex)`${({ theme }) => `
  border: ${theme.borderWidth[1]} dashed ${theme.colours.neutral[5]};
  border-radius: ${theme.borderRadius[2]};
  color: ${theme.colours.primary[1]};
  font-weight: ${theme.fontWeight.bold};
  font-size: ${theme.fontSize[1]};
  padding: ${theme.padding[4]};
  align-items:center;
  justify-content:center;
  cursor: pointer;
`}`;

const AddressHistoryCollapsibleBody = styled(FlexColumn)`${({ theme }) => `
  gap: ${theme.padding[2]};
`}`;

const CopyAddressContainer = styled(Flex)`${({ theme }) => `
  justify-content: end;
  gap:${theme.padding[8]};
`}`;

const CopyAddress = styled(Flex)`${({ theme }) => `
  justify-content: end;
  gap:${theme.padding[1]};
  color: ${theme.colours.primary[1]};
  font-size: ${theme.fontSize[1]};
  font-weight: ${theme.fontWeight.bold};
  cursor: pointer;
`}`;

export default function AddressHistory({
  applicant,
  applicantIndex,
  countries,
  lendingRegions,
}: {
  applicant: Applicant,
  applicantIndex: number,
  countries: StaticDataItem[],
  lendingRegions: StaticDataItem[],
}) {
  const {
    account, appendToAccountArray, creditSearchCompleted,
  } = useAccountContext();

  const {
    applicants, errors, warnings, stage,
  } = account;

  const { addresses } = applicant;

  const [addressAdded, setAddressAdded] = useState<boolean>(false);

  const onClickAddAddress = async () => {
    await appendToAccountArray<Partial<DatedAddress>>(`applicants[${applicantIndex}].addresses`, {
      isCurrentAddress: (applicant?.addresses ?? [])?.length === 0,
    });
    setAddressAdded(true);
  };

  const addressPropertyErrors = errorsToPropertyErrors(errors?.[applicant?.id])?.addresses ?? [];

  const update = useModelFormUpdate();

  const onClickCopyAllAddresses = () => {
    update({
      [`applicants[${applicantIndex}].addresses`]: applicants[0]?.addresses?.map((address) => ({
        ...address,
        id: uuid(),
      })),
    });
  };

  const onClickCopyCurrentAddress = () => {
    const applicant1CurrentAddress = applicants[0]?.addresses?.find((address) => address?.isCurrentAddress);

    if (applicant1CurrentAddress) {
      update({
        [`applicants[${applicantIndex}].addresses`]: [
          {
            ...applicant1CurrentAddress,
            id: uuid(),
          },
          ...(applicant?.addresses?.filter((address) => !address?.isCurrentAddress) ?? []),
        ],
      });
    }
  };

  const listAddressErrors = addresses?.map(
    (address) => (errors?.[address?.address?.id] ?? []),
  )?.flat() ?? [];

  const listAddressesErrors = addresses?.map(
    (address) => (errors?.[address?.id] ?? []),
  )?.flat() ?? [];

  const addressErrors = [...listAddressErrors, ...listAddressesErrors, ...addressPropertyErrors];

  const movedDates = addresses?.reduce((acc, address) => {
    if (address?.movedInDate || address?.movedOutDate) {
      acc.push(address.movedInDate, address.movedOutDate);
    }
    return acc;
  }, [] as string[]) ?? [];

  useToast(
    !addressErrors.length && !!movedDates.length,
    'You have successfully added 3 years of address history.',
    [addressErrors.length, movedDates.join(',')],
  );

  return (
    <PaddedSection
      id={applicant.id}
      propertyName="addresses"
      key={`${applicant.id}-address-history`}
      title="Address history"
      errors={errors}
      warnings={warnings}
      level={1}
      titleIcon={<></>}
    >

      {!stageOnOrAfter(stage, Stages.Affordability) && (
        <CloseInfo>
          <div>Please provide 3 years worth of address history. There cannot be any gaps or overlaps in Applicant&apos;s address history.</div>
          <div>Please ensure that the month and year of each address follow those of the previous address.</div>
        </CloseInfo>
      )}

      <InputGroup><AccountTitle>Address</AccountTitle></InputGroup>

      <AddressHistoryCollapsibleBody>
        {(applicantIndex !== 0 && !creditSearchCompleted) && (
          <CopyAddressContainer>
            <CopyAddress onClick={onClickCopyCurrentAddress}>
              <i className="mi-copy" />
              {' Copy current address of Applicant 1'}
            </CopyAddress>

            <CopyAddress onClick={onClickCopyAllAddresses}>
              <i className="mi-copy" />
              {' Copy all addresses of Applicant 1'}
            </CopyAddress>
          </CopyAddressContainer>
        )}

        <ListContainer>
          {addresses?.map((address, addressIndex) => (
            <AddressHistoryItem
              collapsed={!addressAdded || (address?.id !== addresses?.[addresses.length - 1]?.id)}
              key={address?.id}
              address={address}
              addressIndex={addressIndex}
              applicantIndex={applicantIndex}
              countries={countries}
              lendingRegions={lendingRegions}
            />
          ))}
        </ListContainer>

        {!creditSearchCompleted && (
          <AddAddressContainer onClick={(onClickAddAddress)}>
            <PlusIcon />
            {addresses?.length === 0
              ? 'Please provide 3 years worth of address history.'
              : 'Add address history'}
          </AddAddressContainer>
        )}

      </AddressHistoryCollapsibleBody>
    </PaddedSection>
  );
}
